import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Button, Stepper, Skeleton, Input } from '@vnetwork/ui/primitives';
import { Container, ActionBar } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useMyServices } from '@/api/services';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { useCreateBucket, useBucketNameCheck } from '@/api/s3/buckets';
import { validateBucketName } from './buckets/bucket-name-validation';
import { useCreatePolicy } from '@/api/s3/policies';
import { useCreateAccessKey } from '@/api/s3/access-keys';
import { s3ExtractError } from '@/api/s3';
import type { NewAccessKey } from '@/api/s3/_shared';
import wizardStyles from '../onboarding/OnboardingWizard.module.css';
import { ReviewRow, ReviewGrid } from '../onboarding/onboarding-wizard-parts';
import { STORAGE_PROVIDER, STORAGE_PRODUCT, storageConsoleRoute } from './storage-service';
import {
  STORAGE_STEPS,
  STORAGE_REGION,
  buildPolicyDoc,
  CredentialReveal,
  type PolicyScope,
} from './storage-onboarding-parts';

/**
 * Guided Object Storage onboarding at /services/oss/onboarding: Activate → Bucket
 * → Policy → Credential → Done. Forward-only; every post-activate step is
 * skippable so a customer can bail into the console at any point. Activation
 * mints a per-customer Wasabi sub-account on the `elephant` provider; the later
 * steps drive the standard S3 data-plane hooks against the new service.
 */
export function StorageOnboardingWizard() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();

  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  const { data: providers = [], isLoading: providersLoading } = useProviders();

  const existing = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === STORAGE_PROVIDER),
    [services],
  );
  const elephant = useMemo(
    () =>
      providers.find((p) => p.name?.trim().toLowerCase() === STORAGE_PROVIDER && p.status === 'active'),
    [providers],
  );
  const providerUnavailable = !providersLoading && !existing && !elephant;

  // Freeze the step list on first settle so a mid-flow activation doesn't drop
  // the Activate step. When the org already has a storage service, skip Activate.
  const hadServiceRef = useRef<boolean | null>(null);
  if (!servicesLoading && hadServiceRef.current === null) hadServiceRef.current = !!existing;
  const hadService = hadServiceRef.current === true;
  const steps = hadService ? STORAGE_STEPS.filter((s) => s.id !== 'activate') : STORAGE_STEPS;

  const [stepIndex, setStepIndex] = useState(0);
  const [serviceId, setServiceId] = useState(existing?.id ?? '');
  const [serviceName, setServiceName] = useState<string>(STORAGE_PRODUCT.displayName);
  const [bucketName, setBucketName] = useState('');
  const [createdBucket, setCreatedBucket] = useState('');
  const [policyName, setPolicyName] = useState('');
  const [policyScope, setPolicyScope] = useState<PolicyScope>('readwrite');
  const [createdPolicy, setCreatedPolicy] = useState('');
  const [newKey, setNewKey] = useState<NewAccessKey | null>(null);

  const activate = useActivateService();
  const createBucket = useCreateBucket(serviceId);
  const createPolicy = useCreatePolicy(serviceId);
  const createKey = useCreateAccessKey(serviceId);

  // Debounced global availability check for the (optional) bucket name — mirrors
  // BucketCreate so the user learns a name is taken before the create round-trip.
  const [debouncedBucket, setDebouncedBucket] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBucket(bucketName), 350);
    return () => clearTimeout(t);
  }, [bucketName]);
  const bucketNameError = bucketName.trim() ? validateBucketName(bucketName) : null;
  const bucketSettled =
    !!serviceId && !!bucketName.trim() && !bucketNameError && debouncedBucket === bucketName;
  const bucketCheck = useBucketNameCheck(serviceId, debouncedBucket, bucketSettled);
  const bucketChecking = bucketSettled && bucketCheck.isFetching;
  const bucketTaken = bucketSettled && bucketCheck.data ? !bucketCheck.data.available : false;
  const bucketAvailable = bucketSettled && bucketCheck.data?.available === true;
  const bucketFieldError = bucketNameError ?? (bucketTaken ? 'This bucket name is already taken.' : null);

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const next = () => setStepIndex((i) => i + 1);

  function handleActivate() {
    if (!elephant || !user?.email) return;
    const name = serviceName.trim() || STORAGE_PRODUCT.displayName;
    activate.mutate(
      { serviceName: name, providerId: elephant.id, email: user.email },
      {
        onSuccess: (svc) => {
          setServiceId(svc.id);
          setServiceName(name);
          // WACM mints the account's root key/secret and returns it once at
          // creation — capture it so the Credential step reveals THIS key instead
          // of minting a second, redundant one the customer would have to delete.
          if (svc.accessKeyId && svc.secretAccessKey) {
            setNewKey({ access_key_id: svc.accessKeyId, secret_access_key: svc.secretAccessKey });
          }
          next();
        },
        onError: (err) =>
          show({ tone: 'danger', title: 'Activation failed', description: err.message }),
      },
    );
  }

  function handleBucket() {
    const name = bucketName.trim();
    if (!name) return next();
    if (bucketNameError || bucketTaken || bucketChecking) return;
    createBucket.mutate(
      { name, region: STORAGE_REGION },
      {
        onSuccess: () => {
          setCreatedBucket(name);
          next();
        },
        onError: (err) =>
          show({ tone: 'danger', title: 'Could not create bucket', description: s3ExtractError(err) }),
      },
    );
  }

  function handlePolicy() {
    const name = policyName.trim();
    if (!name) return next();
    createPolicy.mutate(
      { policy_name: name, document: buildPolicyDoc(createdBucket || undefined, policyScope) },
      {
        onSuccess: () => {
          setCreatedPolicy(name);
          next();
        },
        onError: (err) =>
          show({ tone: 'danger', title: 'Could not create policy', description: s3ExtractError(err) }),
      },
    );
  }

  function handleCreateKey() {
    createKey.mutate(undefined, {
      onSuccess: (key) => setNewKey(key),
      onError: (err) =>
        show({ tone: 'danger', title: 'Could not create access key', description: s3ExtractError(err) }),
    });
  }

  function finish() {
    show({ tone: 'success', title: 'Object Storage ready' });
    navigate(storageConsoleRoute(serviceId));
  }

  if (servicesLoading) {
    return (
      <Container size="md">
        <Card>
          <CardBody>
            <Skeleton style={{ height: 240 }} />
          </CardBody>
        </Card>
      </Container>
    );
  }

  const primary = renderPrimary();

  return (
    <Container size="md">
      <Card>
        <CardBody>
          <div className={wizardStyles.wizard}>
            <Stepper steps={steps.map((s) => s.label)} current={stepIndex} reached={stepIndex} />

            <div className={wizardStyles.stepHead}>
              <h2 className={wizardStyles.stepTitle}>{step.title}</h2>
              <p className={wizardStyles.stepDesc}>{step.description}</p>
            </div>

            <div key={step.id} className={wizardStyles.stepBody}>
              {step.id === 'activate' &&
                (providerUnavailable ? (
                  <Alert tone="warning">
                    {STORAGE_PRODUCT.displayName} is not available for self-serve activation on your
                    account yet. Please contact our team to enable it.
                  </Alert>
                ) : (
                  <Field
                    label="Service name"
                    description="A storage account and region are provisioned for you automatically."
                  >
                    {(props) => (
                      <Input
                        {...props}
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                        placeholder={STORAGE_PRODUCT.displayName}
                        disabled={activate.isPending}
                      />
                    )}
                  </Field>
                ))}

              {step.id === 'bucket' && (
                <Field
                  label="Bucket name"
                  error={bucketFieldError ?? undefined}
                  description={
                    bucketChecking
                      ? 'Checking availability…'
                      : bucketAvailable
                        ? 'Name is available.'
                        : 'Lowercase letters, numbers and hyphens. Globally unique.'
                  }
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={bucketName}
                      invalid={!!bucketFieldError}
                      onChange={(e) => setBucketName(e.target.value)}
                      placeholder="my-first-bucket"
                      disabled={createBucket.isPending}
                    />
                  )}
                </Field>
              )}

              {step.id === 'policy' && (
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                  <Field label="Policy name">
                    {(props) => (
                      <Input
                        {...props}
                        value={policyName}
                        onChange={(e) => setPolicyName(e.target.value)}
                        placeholder="app-readwrite"
                        disabled={createPolicy.isPending}
                      />
                    )}
                  </Field>
                  <Field label="Access level" description={createdBucket ? `Scoped to bucket "${createdBucket}".` : 'Applies to all buckets.'}>
                    {() => (
                      <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                        <Button
                          type="button"
                          variant={policyScope === 'read' ? 'primary' : 'secondary'}
                          onClick={() => setPolicyScope('read')}
                        >
                          Read-only
                        </Button>
                        <Button
                          type="button"
                          variant={policyScope === 'readwrite' ? 'primary' : 'secondary'}
                          onClick={() => setPolicyScope('readwrite')}
                        >
                          Read &amp; write
                        </Button>
                      </div>
                    )}
                  </Field>
                </div>
              )}

              {step.id === 'credential' && <CredentialReveal newKey={newKey} />}

              {step.id === 'review' && (
                <ReviewGrid>
                  <ReviewRow label="Service" value={serviceName || '—'} />
                  <ReviewRow label="Bucket" value={createdBucket || 'Skipped'} on={!!createdBucket} />
                  <ReviewRow label="Policy" value={createdPolicy || 'Skipped'} on={!!createdPolicy} />
                  <ReviewRow label="Access key" value={newKey ? 'Created' : 'Skipped'} on={!!newKey} />
                </ReviewGrid>
              )}
            </div>

            <ActionBar
              end={
                <>
                  {step.skippable && !isLast && (
                    <Button variant="ghost" onClick={next}>
                      Skip
                    </Button>
                  )}
                  {primary}
                </>
              }
            />
          </div>
        </CardBody>
      </Card>
    </Container>
  );

  function renderPrimary() {
    switch (step.id) {
      case 'activate':
        return (
          <Button
            disabled={providerUnavailable || !serviceName.trim() || activate.isPending}
            onClick={handleActivate}
          >
            {activate.isPending ? 'Activating…' : 'Activate'}
          </Button>
        );
      case 'bucket':
        return (
          <Button
            disabled={createBucket.isPending || bucketChecking || bucketTaken || !!bucketNameError}
            onClick={handleBucket}
          >
            {createBucket.isPending ? 'Creating…' : bucketName.trim() ? 'Create bucket' : 'Continue'}
          </Button>
        );
      case 'policy':
        return (
          <Button disabled={createPolicy.isPending} onClick={handlePolicy}>
            {createPolicy.isPending ? 'Creating…' : policyName.trim() ? 'Create policy' : 'Continue'}
          </Button>
        );
      case 'credential':
        return newKey ? (
          <Button onClick={next}>Continue</Button>
        ) : (
          <Button disabled={createKey.isPending} onClick={handleCreateKey}>
            {createKey.isPending ? 'Creating…' : 'Create access key'}
          </Button>
        );
      default:
        return <Button onClick={finish}>Go to console</Button>;
    }
  }
}

export default StorageOnboardingWizard;
